import React, { useState, useCallback, useEffect } from 'react';
import { Grid } from '@mui/material';
import HaiPalette from './HaiPalette';
import TehaiArea from './TehaiArea';
import FuroArea from './FuroArea';
import { sortHais } from '../haiUtils';

/**
 * 牌の入力（パレット、手牌、副露）をまとめた共通パネルコンポーネント
 * @param {object} props
 * @param {function({tehai: number[], furos: number[][]})} props.onStateChange - 牌の状態が変更されたときに呼び出されるコールバック
 * @param {React.ReactNode} props.children - パネルの右側カラムに追加で表示する要素（例: 操作ボタン）
 */
const HaiInputPanel = ({ onStateChange, children }) => {
  // --- 状態管理 ---
  const [tehai, setTehai] = useState([]);
  const [furos, setFuros] = useState([[], [], [], []]); // 4つの副露エリア
  const [activeArea, setActiveArea] = useState('tehai'); // 'tehai', 'furo-0', 'furo-1', ...

  // 状態が変更されたら、useEffectを使って親コンポーネントに通知する
  useEffect(() => {
    if (onStateChange) {
      onStateChange({ tehai, furos });
    }
  }, [tehai, furos, onStateChange]);

  /**
   * 牌の追加先エリアを選択するハンドラ
   */
  const handleAreaSelect = useCallback((areaId) => {
    setActiveArea(areaId);
  }, []);

  /**
   * 牌パレットの牌がクリックされたときの処理
   */
  const handlePaletteClick = useCallback((haiId) => {
    if (activeArea === 'tehai') {
      // 手牌に牌を追加する
      setTehai(prev => {
        return prev.length < 14 ? sortHais([...prev, haiId]) : prev;
      });
    } else if (activeArea.startsWith('furo-')) {
      // 副露に牌を追加する
      const furoIndex = parseInt(activeArea.split('-')[1], 10);
      setFuros(prev => {
        const newFuros = [...prev];
        if (newFuros[furoIndex].length < 4) {
          newFuros[furoIndex] = sortHais([...newFuros[furoIndex], haiId]);
        }
        return newFuros;
      });
    }
  }, [activeArea]);

  /**
   * 手牌や副露の牌をクリックで削除する関数
   */
  const handleRemoveHai = useCallback((zoneId, index) => {
    if (zoneId === 'tehai') {
      setTehai(prev => {
        const newTehai = [...prev];
        newTehai.splice(index, 1);
        return sortHais(newTehai);
      });
    } else if (zoneId.startsWith('furo-')) {
      const furoIndex = parseInt(zoneId.split('-')[1], 10);
      setFuros(prev => {
        const newFuros = [...prev];
        const targetFuro = [...newFuros[furoIndex]];
        targetFuro.splice(index, 1);
        newFuros[furoIndex] = sortHais(targetFuro);
        return newFuros;
      });
    }
  }, []);

  return (
    <Grid container spacing={{ xs: 0, md: 4 }}>
      <Grid item xs={12} md={5}>
        <HaiPalette onHaiClick={handlePaletteClick} />
      </Grid>
      <Grid item xs={12} md={7}>
        <TehaiArea
          tehai={tehai}
          onRemove={handleRemoveHai}
          onAreaSelect={handleAreaSelect}
          isActive={activeArea === 'tehai'}
        />
        <FuroArea
          furos={furos}
          onRemove={handleRemoveHai}
          onAreaSelect={handleAreaSelect}
          activeFuro={activeArea.startsWith('furo-') ? parseInt(activeArea.split('-')[1], 10) : null}
        />
        {children}
      </Grid>
    </Grid>
  );
};

export default HaiInputPanel;
